import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, ListGroup, Badge, Spinner, Alert, Button, ButtonGroup } from 'react-bootstrap';
import { getData, checkIn } from '../services/api';
import { events } from '../config/events';
import { useNavigate } from 'react-router-dom';

const CheckInPage = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [checkingIn, setCheckingIn] = useState(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const data = await getData();
      setRegistrations(data.filter((item) => item.type !== 'appointment'));
    } catch (err) {
      setError('Failed to load registrations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCheckIn = async (id, currentStatus) => {
    setCheckingIn(id);
    try {
      await checkIn(id, !currentStatus);
      setRegistrations(prev =>
        prev.map(r => r.id === id ? { ...r, checkedIn: !currentStatus } : r)
      );
    } catch (err) {
      setError('Check-in failed. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setCheckingIn(null);
    }
  };

  const filtered = registrations.filter(r => {
    const matchesEvent = !selectedEvent || r.eventId === selectedEvent;
    const matchesSearch = !search ||
      `${r.firstName} ${r.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      (r.phone && r.phone.includes(search));
    return matchesEvent && matchesSearch;
  });

  const checkedInCount = filtered.filter(r => r.checkedIn).length;

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-3" style={{ maxWidth: 600 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Check-In</h4>
        <div>
          <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => navigate('/admin')}>
            <i className="bi bi-list"></i>
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => { sessionStorage.removeItem('isAuthenticated'); navigate('/login'); }}>
            <i className="bi bi-box-arrow-right"></i>
          </Button>
        </div>
      </div>

      <Form.Select
        className="mb-2"
        value={selectedEvent}
        onChange={e => setSelectedEvent(e.target.value)}
      >
        <option value="">All Events</option>
        {Object.values(events).map(ev => (
          <option key={ev.id} value={ev.id}>{ev.title}</option>
        ))}
      </Form.Select>

      <Form.Control
        type="search"
        placeholder="Search by name or phone..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="mb-3"
        autoFocus
        style={{ fontSize: '1.1rem', padding: '0.75rem' }}
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">{filtered.length} registered</small>
        <Badge bg="success" pill>{checkedInCount} checked in</Badge>
      </div>

      {filtered.length === 0 ? (
        <Alert variant="info">No results found.</Alert>
      ) : (
        <ListGroup>
          {filtered.map(reg => (
            <ListGroup.Item
              key={reg.id}
              className="d-flex justify-content-between align-items-center py-3"
              variant={reg.checkedIn ? 'success' : undefined}
            >
              <div>
                <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  {reg.firstName} {reg.lastName}
                </div>
                {reg.spouseFirstName && (
                  <small className="text-muted">+ {reg.spouseFirstName} {reg.spouseLastName}</small>
                )}
              </div>
              <Button
                variant={reg.checkedIn ? 'outline-secondary' : 'success'}
                size="lg"
                disabled={checkingIn === reg.id}
                onClick={() => handleCheckIn(reg.id, reg.checkedIn)}
                style={{ minWidth: 100 }}
              >
                {checkingIn === reg.id ? (
                  <Spinner animation="border" size="sm" />
                ) : reg.checkedIn ? (
                  'Undo'
                ) : (
                  <><i className="bi bi-check-lg"></i> In</>
                )}
              </Button>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </Container>
  );
};

export default CheckInPage;
